import { useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import {
    createRoleRequest,
    MODULE_LABELS,
    MODULE_ORDER,
    type RoleRequest,
    type RoleRequestType,
    type ModuleKey,
} from '../../mentorshipService';
import './RoleRequestModal.css';

type Props = {
    requestType: RoleRequestType;
    onClose: () => void;
    onSubmitted: (request: RoleRequest) => void;
};

function RoleRequestModal({ requestType, onClose, onSubmitted }: Props) {
    const [selectedModules, setSelectedModules] = useState<ModuleKey[]>([]);
    const [seniority, setSeniority] = useState<'Junior' | 'Mid' | 'Senior'>('Junior');
    const [bio, setBio] = useState('');
    const [linkedin, setLinkedin] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isMentor = requestType === 'become_mentor';
    const title = isMentor ? 'Apply as mentor' : 'Apply as mentee';
    const moduleLabel = isMentor ? 'Specialties' : 'Target modules';
    const moduleHelp = isMentor
        ? 'Pick the framework modules you can review.'
        : 'Pick the framework modules you want to focus on.';

    const toggleModule = (m: ModuleKey) => {
        setSelectedModules((prev) =>
            prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m],
        );
    };

    const canSubmit =
        bio.trim().length > 0 &&
        selectedModules.length > 0 &&
        !isSaving;

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setIsSaving(true);
        setError(null);
        try {
            const payload: Record<string, unknown> = {
                bio: bio.trim(),
            };
            if (isMentor) {
                payload.specialties = selectedModules;
                if (linkedin.trim()) payload.linkedin = linkedin.trim();
            } else {
                payload.target_modules = selectedModules;
                payload.seniority = seniority;
            }
            const request = await createRoleRequest(requestType, payload);
            onSubmitted(request);
        } catch (err) {
            console.error('[RoleRequestModal] failed to submit', err);
            const message = err instanceof Error ? err.message : 'Submission failed';
            setError(message);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal open={true} onClose={onClose} size="md" ariaLabel={title}>
            <Modal.Header onClose={onClose}>{title}</Modal.Header>
            <Modal.Body>
                <div className="role-request-form__field">
                    <label className="role-request-form__label">{moduleLabel}</label>
                    <p className="role-request-form__help">{moduleHelp}</p>
                    <div className="role-request-form__module-grid">
                        {MODULE_ORDER.map((m) => {
                            const checked = selectedModules.includes(m);
                            return (
                                <button
                                    key={m}
                                    type="button"
                                    className={`role-request-form__module-chip ${
                                        checked ? 'is-checked' : ''
                                    }`}
                                    onClick={() => toggleModule(m)}
                                    aria-pressed={checked ? 'true' : 'false'}
                                >
                                    {MODULE_LABELS[m]}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {!isMentor && (
                    <div className="role-request-form__field">
                        <label className="role-request-form__label" htmlFor="rr-seniority">
                            Self-assessed seniority
                        </label>
                        <select
                            id="rr-seniority"
                            className="role-request-form__select"
                            value={seniority}
                            onChange={(e) =>
                                setSeniority(e.target.value as 'Junior' | 'Mid' | 'Senior')
                            }
                        >
                            <option value="Junior">Junior</option>
                            <option value="Mid">Mid</option>
                            <option value="Senior">Senior</option>
                        </select>
                    </div>
                )}

                <div className="role-request-form__field">
                    <label className="role-request-form__label" htmlFor="rr-bio">
                        Brief bio
                    </label>
                    <p className="role-request-form__help">
                        A few sentences about your background and what you're hoping to
                        get out of the program.
                    </p>
                    <textarea
                        id="rr-bio"
                        className="role-request-form__textarea"
                        value={bio}
                        onChange={(e) => setBio(e.target.value)}
                        placeholder="Tell us about yourself…"
                        rows={5}
                    />
                </div>

                {isMentor && (
                    <div className="role-request-form__field">
                        <label className="role-request-form__label" htmlFor="rr-linkedin">
                            LinkedIn (optional)
                        </label>
                        <input
                            id="rr-linkedin"
                            type="url"
                            className="role-request-form__input"
                            value={linkedin}
                            onChange={(e) => setLinkedin(e.target.value)}
                            placeholder="https://linkedin.com/in/…"
                        />
                    </div>
                )}

                {error && <p className="role-request-form__error">{error}</p>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="ghost" size="md" onClick={onClose} disabled={isSaving}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    size="md"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                >
                    {isSaving ? 'Submitting…' : 'Submit application'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default RoleRequestModal;
